/**
 * Login Page
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert, Key, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';

interface LoginProps {
  onLogin: () => void;
}

export function Login({ onLogin }: LoginProps) {
  const { t } = useTranslation();
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Call the login endpoint which will set the HttpOnly cookie
      await api.login(token.trim());

      // Update authentication state
      api.setAuthenticationState(true);

      // If successful, call onLogin
      onLogin();
    } catch (err) {
      api.setAuthenticationState(false);
      setError(
        err instanceof Error
          ? err.message
          : t('login.invalidToken')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 text-white">
            <ShieldAlert size={48} className="fill-primary-300" />
            <div className="text-left">
              <h1 className="text-3xl font-bold">{t('common.appName')}</h1>
              <p className="text-primary-300 text-sm">{t('common.adminDashboard')}</p>
            </div>
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('login.title')}</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {t('login.subtitle')}
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="token"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                {t('login.adminToken')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Key size={18} className="text-gray-400" />
                </div>
                <input
                  id="token"
                  type={showToken ? 'text' : 'password'}
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder={t('login.tokenPlaceholder')}
                  className="w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  {showToken ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !token.trim()}
              className="w-full py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="spinner h-4 w-4 border-white border-t-transparent" />
                  {t('login.verifying')}
                </span>
              ) : (
                t('login.signIn')
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              {t('login.noToken')}{' '}
              <a
                href="https://github.com/your-repo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
              >
                {t('login.viewDocs')}
              </a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-primary-300 text-sm mt-8">
          {t('common.appName')} v{__APP_VERSION__}
        </p>
      </div>
    </div>
  );
}

export default Login;

